import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import 'datatables.net-dt/css/dataTables.dataTables.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'datatables.net';
import { authManager } from '../utils/auth';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const limit = 10;
const $ = window.$;

// Single source of truth for the filters' empty shape, used by both the
// useState initializer and handleReset so they can't drift apart if a
// filter field is ever added/renamed.
const INITIAL_FILTERS = {
    referralSource: '', utmCampaign: '', plan: '', status: '',
    email: '', lastLogin: '', dateFrom: '', dateTo: '',
    // Loyalty snapshot filters (Anton's "5 fields" segmentation) — backed by
    // the denormalized loyalty* fields on the user record.
    hasScanned: '', minReceipts: '', everRedeemed: '', minBalanceDollars: '', minPointsEarned: '',
    lastReceiptFrom: '', lastReceiptTo: '',
};

// CSV export is built client-side: there is no /users/export endpoint, so
// CSV Download pages through the full filtered result set from /users/list
// (using the exact same query the table last ran) and formats each row the
// way the on-screen table does — plus the acquisition / ad-attribution
// fields that until now only the user detail view surfaced.
const CSV_PAGE_SIZE = 500;
// Hard ceiling on pages fetched so a bad pagination response can't loop forever.
const CSV_MAX_PAGES = 400;

// RFC-4180 quoting + a leading-quote guard against CSV/formula injection when
// the file is opened in a spreadsheet (same reason the table renders utm_*
// as text, not HTML — these values come verbatim from query params).
const csvCell = (value) => {
    if (value === null || value === undefined) return '';
    let str = String(value);
    if (/^[=+\-@\t\r]/.test(str)) str = `'${str}`;
    return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const csvDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '');
// Loyalty balance is stored (and returned by /users/list) as whole cents,
// already rounded down by squad-rewards — issue #647 wants it read as a
// cash figure, never raw points.
const dollars = (cents) => `$${((Number.isFinite(cents) ? cents : 0) / 100).toFixed(2)}`;
const csvDateTime = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

// Mirrors planLabel() in the table's ajax handler (plain text, no markup).
const csvPlanLabel = (user) => {
    const s = user.subscriptionStatus;
    const planType = user.currentPlan?.planType;
    const isPaidPlan = planType && planType !== 'free';
    if (s === 'active' && isPaidPlan) return 'Paid';
    if (s === 'trialing' && isPaidPlan) return 'Trial';
    return 'Free';
};

// Column order: identity/status first (as shown in the table), then the
// Acquisition block in the same order as the user detail view.
const CSV_COLUMNS = [
    { header: 'S.No', value: (u, i) => i + 1 },
    { header: 'Name', value: (u) => u.name },
    { header: 'Email', value: (u) => u.email },
    { header: 'Role', value: (u) => u.role },
    { header: 'Source', value: (u) => u.source || 'circleone' },
    { header: 'Plan', value: (u) => csvPlanLabel(u) },
    { header: 'Created Date', value: (u) => csvDate(u.createdAt) },
    { header: 'Last Login', value: (u) => csvDate(u.lastActivity) },
    { header: 'Status', value: (u) => (u.status === 'active' ? 'Active' : 'Inactive') },
    { header: 'Acquisition Funnel', value: (u) => u.acquisition_funnel },
    { header: 'Last Funnel Touch', value: (u) => u.last_funnel_touch },
    { header: 'Last Funnel Touch At', value: (u) => csvDateTime(u.last_funnel_touch_at) },
    { header: 'UTM Source', value: (u) => u.acquisition_utm_source },
    { header: 'UTM Medium', value: (u) => u.acquisition_utm_medium },
    { header: 'UTM Campaign', value: (u) => u.acquisition_utm_campaign },
    { header: 'UTM Content', value: (u) => u.acquisition_utm_content },
    { header: 'UTM Term', value: (u) => u.acquisition_utm_term },
    { header: 'Facebook Click ID (fbclid)', value: (u) => u.acquisition_fbclid },
    { header: 'Facebook Click ID (fbc)', value: (u) => u.acquisition_fbc },
    { header: 'Google Click ID (gclid)', value: (u) => u.acquisition_gclid },
    { header: 'Referral Code Used', value: (u) => u.acquisition_ref_code },
    { header: 'Provisional', value: (u) => (u.acquisition_provisional ? 'Yes' : 'No') },
    // Loyalty snapshot (denormalized loyalty* fields on the user record).
    { header: 'Has Scanned', value: (u) => (u.loyaltyHasScanned ? 'Yes' : 'No') },
    { header: 'Receipts', value: (u) => u.loyaltyReceiptCount ?? 0 },
    { header: 'Last Receipt Date', value: (u) => csvDate(u.loyaltyLastReceiptAt) },
    { header: 'Points Earned', value: (u) => (Number.isFinite(u.loyaltyPointsEarned) ? u.loyaltyPointsEarned : 0) },
    { header: 'Balance', value: (u) => dollars(u.loyaltyBalanceCents) },
    { header: 'Ever Redeemed', value: (u) => (u.loyaltyEverRedeemed ? 'Yes' : 'No') },
];

const buildUsersCsv = (users) => {
    const header = CSV_COLUMNS.map((c) => csvCell(c.header)).join(',');
    const rows = users.map((u, i) => CSV_COLUMNS.map((c) => csvCell(c.value(u, i))).join(','));
    return [header, ...rows].join('\r\n');
};

// Shared by the DataTable's ajax call and the CSV download button so both
// always request the exact same filtered set.
const buildFilterParams = (filterRole, filterSources, filterValues) => {
    const params = new URLSearchParams();
    if (filterRole) params.set('role', filterRole);

    const specificSources = (filterSources || []).filter(s => s !== 'all');
    if (specificSources.length) params.set('source', specificSources.join(','));

    const {
        referralSource, utmCampaign, plan, status, email, lastLogin, dateFrom, dateTo,
        hasScanned, minReceipts, everRedeemed, minBalanceDollars, minPointsEarned,
        lastReceiptFrom, lastReceiptTo,
    } = filterValues || {};
    if (referralSource) params.set('referralSource', referralSource);
    if (utmCampaign) params.set('utmCampaign', utmCampaign);
    if (plan) params.set('plan', plan);
    if (status) params.set('userStatus', status);
    if (email) params.set('email', email);
    if (lastLogin) params.set('lastLogin', lastLogin);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (hasScanned) params.set('hasScanned', hasScanned);
    if (minReceipts) params.set('minReceipts', minReceipts);
    if (everRedeemed) params.set('everRedeemed', everRedeemed);
    if (minBalanceDollars) params.set('minBalanceDollars', minBalanceDollars);
    if (minPointsEarned) params.set('minPointsEarned', minPointsEarned);
    if (lastReceiptFrom) params.set('lastReceiptFrom', lastReceiptFrom);
    if (lastReceiptTo) params.set('lastReceiptTo', lastReceiptTo);

    return params;
};

const User = () => {
    const navigate = useNavigate();
    const [selectedUser, setSelectedUser] = useState({ id: '', name: '' });

    const [role, setRole] = useState('');
    const [sources, setSources] = useState([]);
    const [currentUserRole, setCurrentUserRole] = useState(null);

    const [filters, setFilters] = useState(INITIAL_FILTERS);

    // The exact query params the table's last request used (filters, DataTables'
    // own free-text search box, sort), captured from inside the ajax callback so
    // CSV Download can request precisely what's on screen — not the live (possibly
    // unsubmitted) filter state, and not missing the DataTables search box, which
    // buildFilterParams alone doesn't know about.
    const [lastQueryParams, setLastQueryParams] = useState(null);
    const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const SOURCE_FILTERS = [
        { value: 'all', label: 'All' },
        { value: 'circleone', label: 'Circle One' },
        { value: 'mml', label: 'MML' },
        { value: 'somocares', label: 'Somo Cares' },
    ];

    const toggleSource = (val) => {
        if (val === 'all') {
            // All = clear specific selections (no source param sent = show everyone)
            setSources(prev => prev.includes('all') ? [] : ['all']);
        } else {
            // Selecting a specific source clears "All"
            setSources(prev => {
                const without = prev.filter(s => s !== 'all' && s !== val);
                return prev.includes(val) ? without : [...without, val];
            });
        }
    };

    useEffect(() => {
        authManager.validateUserRole()
            .then(setCurrentUserRole)
            .catch(() => setCurrentUserRole(null));
    }, []);

    const destroyDataTable = useCallback(() => {
        if ($.fn.DataTable && $.fn.DataTable.isDataTable('#users')) {
            $('#users').DataTable().clear().destroy();
        }
    }, []);


    const toggleRole = (roleValue) => {
        setRole(prev => (prev === roleValue ? '' : roleValue));
    };

    const initializeDataTable = useCallback((filterRole, filterSources, filterValues) => {
        destroyDataTable();

        $('#users').DataTable({
            serverSide: true,
            processing: true,
            ajax: async (dataTablesParams, callback) => {
                const start = dataTablesParams.start || 0;
                const page = Math.floor(start / limit) + 1;
                const searchValue = dataTablesParams.search?.value || '';

                const columnFieldMap = {
                    9: 'createdAt', 10: 'lastActivity',
                    13: 'loyaltyReceiptCount', 14: 'loyaltyLastReceiptAt',
                    15: 'loyaltyPointsEarned', 16: 'loyaltyBalanceCents',
                };
                const sortColumnIndex = dataTablesParams.order?.[0]?.column;
                const sortBy = columnFieldMap[sortColumnIndex] || 'createdAt';
                const sortDirection = dataTablesParams.order?.[0]?.dir || 'desc';

                try {
                    const params = buildFilterParams(filterRole, filterSources, filterValues);
                    params.set('page', page);
                    params.set('limit', limit);
                    params.set('search', searchValue);
                    params.set('sortBy', sortBy);
                    params.set('sortOrder', sortDirection);
                    const url = `${Base_Url}/users/list?${params.toString()}`;
                    setLastQueryParams(params);

                    const response = await authManager.authenticatedFetch(url);
                    const json = await response.json();

                    const users = json.result?.users || [];
                    const totalRecords = json.result?.pagination?.total ?? users.length;
                    let filteredUsers = users;

                    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '-';

                    // "Paid" requires an active/trialing subscription on a non-free
                    // plan — subscription_status alone is 'active' even for the
                    // free tier once it's provisioned, which previously showed
                    // every free signup as "Paid".
                    const planLabel = (user) => {
                        const s = user.subscriptionStatus;
                        const planType = user.currentPlan?.planType;
                        const isPaidPlan = planType && planType !== 'free';
                        if (s === 'active' && isPaidPlan) return '<span class="f_green fwt_600">Paid</span>';
                        if (s === 'trialing' && isPaidPlan) return '<span class="text-warning fwt_600">Trial</span>';
                        return '<span class="text-muted">Free</span>';
                    };

                    const transformed = filteredUsers.map((user, i) => ({
                        serial: start + i + 1,
                        name: user.name || '-',
                        email: user.email || '-',
                        twofa: user.mfaEnabled ? '<span class="f_green fwt_600">Enabled</span>' : '-',
                        role: user.role || '-',
                        source: user.source || 'circleone',
                        referralSource: user.acquisition_utm_source || '-',
                        utmCampaign: user.acquisition_utm_campaign || '-',
                        plan: planLabel(user),
                        createdAt: formatDate(user.createdAt),
                        lastLogin: formatDate(user.lastActivity),
                        status:
                            user.status === 'active'
                                ? '<span class="f_green fwt_600">Active</span>'
                                : '<span class="f_red fwt_600">Inactive</span>',
                        hasScanned: user.loyaltyHasScanned
                            ? '<span class="f_green fwt_600">Yes</span>'
                            : '<span class="text-muted">No</span>',
                        receipts: user.loyaltyReceiptCount ?? 0,
                        lastReceipt: formatDate(user.loyaltyLastReceiptAt),
                        earned: Number.isFinite(user.loyaltyPointsEarned) ? user.loyaltyPointsEarned : 0,
                        balance: dollars(user.loyaltyBalanceCents),
                        everRedeemed: user.loyaltyEverRedeemed
                            ? '<span class="f_green fwt_600">Yes</span>'
                            : '<span class="text-muted">No</span>',
                        _id: user._id,
                        _email: user.email,
                    }));

                    callback({
                        recordsTotal: totalRecords,
                        recordsFiltered: totalRecords,
                        data: transformed,
                    });
                } catch (err) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error('Error fetching users:', err);
                    }
                    callback({ recordsTotal: 0, recordsFiltered: 0, data: [] });
                }
            },
            columns: [
                { data: 'serial', title: 'S.No', className: 'text-center', orderable: false, width: '60px' },
                { data: 'name', title: 'Name', orderable: false },
                { data: 'email', title: 'Email', orderable: false },
                { data: 'twofa', title: '2FA', className: 'text-center', orderable: false },
                { data: 'role', title: 'Role', orderable: false },
                { data: 'source', title: 'Source', orderable: false },
                {
                    data: 'referralSource',
                    title: 'Referral Source',
                    orderable: false,
                    // acquisition_utm_source is taken verbatim from a ?utm_source= query
                    // param with no HTML sanitization on the backend — unlike name/email,
                    // it isn't validated against markup. DataTables renders cell data as
                    // raw HTML by default, so a crafted signup link could otherwise run
                    // script in an admin's session when they view this table.
                    render: $.fn.dataTable.render.text(),
                },
                {
                    data: 'utmCampaign',
                    title: 'UTM Campaign',
                    orderable: false,
                    // Same as Referral Source: acquisition_utm_campaign is stored verbatim
                    // from ?utm_campaign= with no markup sanitization, so it must be rendered
                    // as text, not raw HTML.
                    render: $.fn.dataTable.render.text(),
                },
                { data: 'plan', title: 'Plan', className: 'text-center', orderable: false },
                { data: 'createdAt', title: 'Created Date', className: 'text-center', orderable: true },
                { data: 'lastLogin', title: 'Last Login', className: 'text-center', orderable: true },
                { data: 'status', title: 'Status', className: 'text-center', orderable: false },
                { data: 'hasScanned', title: 'Scanned', className: 'text-center', orderable: false },
                { data: 'receipts', title: 'Receipts', className: 'text-center', orderable: true },
                { data: 'lastReceipt', title: 'Last Receipt', className: 'text-center', orderable: true },
                { data: 'earned', title: 'Earned', className: 'text-center', orderable: true },
                { data: 'balance', title: 'Balance', className: 'text-center', orderable: true },
                { data: 'everRedeemed', title: 'Redeemed', className: 'text-center', orderable: false },
                {
                    data: '_id',
                    title: 'Action',
                    className: 'text-center',
                    orderable: false,
                    render: (data, type, row) => {
                        return `
              <div align="center">
                <i class="fa-solid fa-eye mr-2 crsr_pntr action-icon" data-navigate="/users/${row._id}"></i>
                <i class="fa-solid fa-pen mr-2 crsr_pntr action-icon" data-navigate="/usersedit/${row._id}"></i>
                <i class="fa-solid fa-trash-can crsr_pntr action-icon" data-id="${row._id}" data-name="${row.name}"></i>
              </div>`;
                    },
                },
            ],
            pageLength: limit,
            lengthChange: false,
            responsive: true,
            autoWidth: false,
            paging: true,
            ordering: true,
            // Without this, DataTables defaults its initial `order` to column 0
            // ascending (even though S.No isn't orderable) — that stray 'asc' was
            // being picked up as the sort direction while sortBy still resolved
            // to 'createdAt', so the list loaded oldest-first instead of newest.
            order: [[9, 'desc']],
            searching: true,
            pagingType: 'simple_numbers',
            language: { paginate: { next: 'Next', previous: 'Previous' } },
            drawCallback: function () {
                const tablePosition = document.getElementById('users')?.offsetTop;
                if (tablePosition !== undefined) {
                    window.scrollTo({ top: tablePosition - 100, behavior: 'smooth' });
                }
            },
        });
    }, [destroyDataTable]);

    useEffect(() => {
        const table = document.getElementById('users');
        if (!table) return;

        const handleClick = (e) => {
            const target = e.target.closest('[data-navigate], [data-id]');
            if (!target) return;

            if (target?.hasAttribute('data-id')) {
                e.preventDefault();
                const id = target.getAttribute('data-id');
                const name = target.getAttribute('data-name');
                setSelectedUser({ id, name });
                $('#modaldelete').modal('show');
                return;
            }
            if (target?.hasAttribute('data-navigate')) {
                navigate(target.getAttribute('data-navigate'));
            }
        };

        table.addEventListener('click', handleClick);
        return () => table.removeEventListener('click', handleClick);
    }, [navigate]);

    const handleModalClose = () => {
        $('#modaldelete').modal('hide');
        setTimeout(() => {
            $('.modal-backdrop').remove();
            $('body').removeClass('modal-open');
            $('body').css('overflow', '');
            $('body').css('padding-right', '');
        }, 300);
        setSelectedUser({ id: '', name: '' });
    };

    const handleDelete = async () => {
        if (!selectedUser.id) return;
        try {
            const response = await authManager.authenticatedFetch(`${Base_Url}/users/${selectedUser.id}`, {
                method: 'DELETE',
            });
            const data = await response.json();

            if (data?.statusCode === 200 || data?.status === 'success') {
                handleModalClose();
                toast.success('User deleted successfully!');
                setSelectedUser({ id: '', name: '' });
                initializeDataTable(role, sources, filters);
            } else {
                toast.error(data?.message || 'Error processing deletion.');
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Delete error:', error);
            }
            toast.error('Something went wrong while deleting the user');
        }
    };

    // Intentional empty deps: DataTable is initialised once on mount with no filters.
    // Filters (role, sources, filters) are applied explicitly via the Search button
    // (handleSearchSubmit) or Reset button (handleReset) — both pass current values
    // as arguments to initializeDataTable(filterRole, filterSources, filterValues) to
    // avoid stale-closure issues. Auto-reinitialising on every checkbox/input change
    // would send a server request per keystroke and is intentionally avoided here.
    useEffect(() => {
        initializeDataTable('', [], INITIAL_FILTERS);
        return () => {
            $('.modal-backdrop').remove();
            $('body').removeClass('modal-open');
            $('body').css('overflow', '');
            $('body').css('padding-right', '');
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleReset = () => {
        setRole('');
        setSources([]);
        setFilters(INITIAL_FILTERS);
        initializeDataTable('', [], INITIAL_FILTERS);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        initializeDataTable(role, sources, filters);
    };

    const handleDownloadCsv = async () => {
        if (isDownloadingCsv) return; // guards against a fast double-click firing two exports
        setIsDownloadingCsv(true);
        try {
            // Export exactly what the table is currently showing: the last query
            // it actually ran (filters + DataTables search box + sort), not live
            // (possibly unsubmitted) filter state. Falls back to the plain filter
            // params only in the unlikely case the table's first request hasn't
            // resolved yet.
            const params = lastQueryParams
                ? new URLSearchParams(lastQueryParams)
                : buildFilterParams(role, sources, filters);
            params.set('limit', CSV_PAGE_SIZE);

            // No /users/export endpoint — page through /users/list and build the
            // CSV in the browser.
            const allUsers = [];
            let page = 1;
            let totalPages = 1;
            do {
                params.set('page', page);
                const response = await authManager.authenticatedFetch(`${Base_Url}/users/list?${params.toString()}`);
                if (!response.ok) {
                    toast.error('Failed to download users CSV');
                    return;
                }
                const json = await response.json();
                allUsers.push(...(json.result?.users || []));
                totalPages = json.result?.pagination?.pages || 1;
                page += 1;
            } while (page <= totalPages && page <= CSV_MAX_PAGES);

            const csv = buildUsersCsv(allUsers);
            // Leading BOM (U+FEFF) so Excel reads the file as UTF-8 (accented names etc.).
            const blob = new Blob([String.fromCharCode(0xFEFF) + csv], { type: 'text/csv;charset=utf-8;' });
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            // Date-stamped (local date, ISO order) so repeated exports don't collide
            // in the browser's downloads folder and each file says when it was pulled.
            link.download = `users_${new Date().toLocaleDateString('en-CA')}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error('CSV download error:', err);
            }
            toast.error('Something went wrong while downloading the CSV');
        } finally {
            setIsDownloadingCsv(false);
        }
    };

    return (
        <div>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Users</h1>
                        </div>
                        <div className="col-sm-6 txt_rgt">
                            <button className="btn btn-blue" onClick={() => navigate('/inviteUser')}>
                                Invite User(s)
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card card-primary card-outline">
                                <form className="form-horizontal" onSubmit={handleSearchSubmit}>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Role</label>
                                                    <div className="col-12 col-md-9 d-flex align-items-center flex-wrap">
                                                        {currentUserRole === 'superadmin' && (
                                                            <div className="form-check mr-3">
                                                                <input type="checkbox" id="role_superadmin" className="form-check-input" checked={role === 'superadmin'} onChange={() => toggleRole('superadmin')} />
                                                                <label className="form-check-label" htmlFor="role_superadmin">Super Admin</label>
                                                            </div>
                                                        )}
                                                        <div className="form-check mr-3">
                                                            <input type="checkbox" id="role_admin" className="form-check-input" checked={role === 'admin'} onChange={() => toggleRole('admin')} />
                                                            <label className="form-check-label" htmlFor="role_admin">Admin</label>
                                                        </div>
                                                        <div className="form-check mr-3">
                                                            <input type="checkbox" id="role_user" className="form-check-input" checked={role === 'user'} onChange={() => toggleRole('user')} />
                                                            <label className="form-check-label" htmlFor="role_user">User</label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {currentUserRole === 'superadmin' && (
                                                <div className="col-12 col-lg-6">
                                                    <div className="form-group row align-items-center">
                                                        <label className="col-12 col-md-3 col-form-label">Source</label>
                                                        <div className="col-12 col-md-9 d-flex align-items-center flex-wrap" style={{ gap: '16px' }}>
                                                            {SOURCE_FILTERS.map(s => (
                                                                <div className="form-check" key={s.value}>
                                                                    <input type="checkbox" id={`src_${s.value}`} className="form-check-input" checked={sources.includes(s.value)} onChange={() => toggleSource(s.value)} />
                                                                    <label className="form-check-label" htmlFor={`src_${s.value}`}>{s.label}</label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Referral Source</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="text" className="form-control" name="referralSource" value={filters.referralSource} onChange={handleFilterChange} placeholder="Search referral source" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">UTM Campaign</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="text" className="form-control" name="utmCampaign" value={filters.utmCampaign} onChange={handleFilterChange} placeholder="Search UTM campaign" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Plan</label>
                                                    <div className="col-12 col-md-9">
                                                        <select className="form-control" name="plan" value={filters.plan} onChange={handleFilterChange}>
                                                            <option value="">--Select--</option>
                                                            <option value="free">Free</option>
                                                            <option value="trial">Trial</option>
                                                            <option value="paid">Paid</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Status</label>
                                                    <div className="col-12 col-md-9">
                                                        <select className="form-control" name="status" value={filters.status} onChange={handleFilterChange}>
                                                            <option value="">--Select--</option>
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Last Login</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="date" className="form-control" name="lastLogin" value={filters.lastLogin} onChange={handleFilterChange} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Email</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="text" className="form-control" name="email" value={filters.email} onChange={handleFilterChange} placeholder="Search email" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">From Date</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="date" className="form-control" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">To Date</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="date" className="form-control" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Has Scanned</label>
                                                    <div className="col-12 col-md-9">
                                                        <select className="form-control" name="hasScanned" value={filters.hasScanned} onChange={handleFilterChange}>
                                                            <option value="">--Select--</option>
                                                            <option value="true">Yes — activated</option>
                                                            <option value="false">No — not activated</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Min Receipts</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="number" min="0" className="form-control" name="minReceipts" value={filters.minReceipts} onChange={handleFilterChange} placeholder="e.g. 10 for heavy scanners" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Ever Redeemed</label>
                                                    <div className="col-12 col-md-9">
                                                        <select className="form-control" name="everRedeemed" value={filters.everRedeemed} onChange={handleFilterChange}>
                                                            <option value="">--Select--</option>
                                                            <option value="true">Yes</option>
                                                            <option value="false">No — never redeemed</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Min Balance ($)</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="number" min="0" step="0.01" className="form-control" name="minBalanceDollars" value={filters.minBalanceDollars} onChange={handleFilterChange} placeholder="e.g. 10 for members with $10+ banked" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Min Earned</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="number" min="0" step="any" className="form-control" name="minPointsEarned" value={filters.minPointsEarned} onChange={handleFilterChange} placeholder="Minimum lifetime points earned" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Last Receipt From</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="date" className="form-control" name="lastReceiptFrom" value={filters.lastReceiptFrom} onChange={handleFilterChange} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-lg-6">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-3 col-form-label">Last Receipt Before</label>
                                                    <div className="col-12 col-md-9">
                                                        <input type="date" className="form-control" name="lastReceiptTo" value={filters.lastReceiptTo} onChange={handleFilterChange} />
                                                        <small className="text-muted">Nothing scanned since this date = dormant</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-footer text-center">
                                        <button type="button" className="btn btn-secondary mr-2" onClick={handleReset}>Reset</button>
                                        <button type="submit" className="btn btn-blue mr-2">Search</button>
                                        <button type="button" className="btn btn-blue" onClick={handleDownloadCsv} disabled={isDownloadingCsv}>
                                            {isDownloadingCsv ? 'Downloading…' : 'CSV Download'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12">
                            <div className="table-responsive">
                                <table id="users" className="table table-striped table-bordered txt_lft" style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th>S.No</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>2FA</th>
                                            <th>Role</th>
                                            <th>Source</th>
                                            <th>Referral Source</th>
                                            <th>UTM Campaign</th>
                                            <th>Plan</th>
                                            <th>Created Date</th>
                                            <th>Last Login</th>
                                            <th>Status</th>
                                            <th>Scanned</th>
                                            <th>Receipts</th>
                                            <th>Last Receipt</th>
                                            <th>Earned</th>
                                            <th>Balance</th>
                                            <th>Redeemed</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody />
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                className="modal fade"
                id="modaldelete"
                tabIndex={-1}
                aria-labelledby="modaldeleteLabel"
                aria-hidden="true"
                data-backdrop="static"
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="modaldeleteLabel">
                                Delete User
                            </h5>
                            <button type="button" className="close" onClick={handleModalClose} aria-label="Close">
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="w-100 d-inline-block text-center">
                                Are you sure you want to delete <br />
                                <span className="fwt_600">{selectedUser?.name || '-'}</span>?
                            </div>
                        </div>
                        <div className="modal-footer justify-content-center">
                            <button type="button" className="btn btn-danger" onClick={handleDelete}>
                                Yes, delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default User;